import React, { useRef, useEffect } from 'react'
import styled, { createGlobalStyle } from 'styled-components'
import I18n from '../../../../core/i18n'
import moment from 'moment'
import 'moment/locale/ja'
import { CheckBox } from '../../../atoms'
import * as interfaces from '../../../../static/interfaces'
import * as constants from 'static/constants'
import FilterModalBody from 'components/organisms/Tours/Weekly/FilterModalBody'
import SvgIcon from 'components/atoms/SvgIcon'
import { buildFilterLabels, clearUrlFilterOptions } from 'utils/weekly'
import { IFilterOptions } from 'utils/weeklyTypes'
import { SingleDatePicker } from 'react-dates'

declare var window: interfaces.IWindow

interface IWeeklySettingsProps {
  filterOptions: IFilterOptions
  posts: any[]
  startDate: moment.Moment
  setFilterOptions: (filterOptions: IFilterOptions) => void
}
const urlParams = new URLSearchParams(window.location.search)
const today = moment()
const LIMIT_GUIDE_ASSIGN_CSV_DAYS = 90
const buildGuideListExportQuery = (
  startDate: moment.Moment,
  range: number,
  filterOptions: IFilterOptions
) => {
  if (range <= 0) {
    window.flashMessages.addMessage({
      text: '0より大きい値を指定してください',
      type: 'error',
    })
    return
  }

  const query = new URLSearchParams()
  query.set('start_date', startDate.format('YYYY-MM-DD'))

  // 90日間分のガイドアサイン状況CSVを取得
  // 今後変更の際には引数などで受け取る形とする
  query.set(
    'end_date',
    startDate
      .clone()
      .add(range - 1, 'days')
      .format('YYYY-MM-DD')
  )

  filterOptions.post_ids.forEach(id => query.append('post_ids[]', String(id)))
  filterOptions.post_types.forEach(postType => query.append('post_types[]', postType))
  filterOptions.tour_group_status.forEach(status => query.append('tour_group_status[]', status))

  const booleanFilters: Array<[keyof IFilterOptions, boolean | undefined]> = [
    ['alert', filterOptions.alert],
    ['insufficient_with_guide', filterOptions.insufficient_with_guide],
    ['insufficient_with_no_guide', filterOptions.insufficient_with_no_guide],
    ['connected', filterOptions.connected],
    ['not_connected', filterOptions.not_connected],
    ['all_task_done', filterOptions.all_task_done],
    ['has_task', filterOptions.has_task],
  ]
  booleanFilters.forEach(([key, value]) => {
    if (value !== undefined) {
      query.set(key, String(value))
    }
  })

  return query.toString()
}

const WeeklySettings = React.memo(
  ({ filterOptions, posts, startDate, setFilterOptions }: IWeeklySettingsProps) => {
    const [showDropdown, setShowDropdown] = React.useState(false)
    const [focused, setFocused] = React.useState<boolean>(false)
    const [includeNoBooking, setIncludeNoBooking] = React.useState(
      urlParams.get('include_no_booking') === 'true'
    )

    const currentDateLabel = React.useMemo(() => {
      const startLabel = startDate.format('YYYY/M/D')
      const endLabel = startDate
        .clone()
        .add(6, 'days')
        .format('YYYY/MM/DD')

      return `${startLabel}-${endLabel}`
    }, [startDate])

    const handleClickRangeButton = (startDay: moment.Moment) => {
      const currentUrlParams = new URLSearchParams(window.location.search)
      currentUrlParams.set('start', startDay.format('YYYY-MM-DD'))
      currentUrlParams.delete('include_no_booking')

      location.href = `/agent/tours/weekly?${currentUrlParams.toString()}`
    }

    const showFilterModal = React.useCallback(() => {
      window.globalModal.showModal({
        title: 'フィルター選択',
        body: (
          <FilterModalBody
            posts={posts.filter(post => post.state !== 'draft')}
            filterOptions={filterOptions}
            setFilterOptions={setFilterOptions}
          />
        ),
        submitText: '',
        handleSubmit: undefined,
      })
    }, [filterOptions, setFilterOptions])

    const handleClickClearFilter = () => {
      setFilterOptions({
        post_ids: [],
        post_types: [],
        tour_group_status: [],
        alert: undefined,
        insufficient_with_guide: undefined,
        insufficient_with_no_guide: undefined,
        connected: undefined,
        not_connected: undefined,
        all_task_done: undefined,
        has_task: undefined,
      })
      clearUrlFilterOptions(filterOptions)
    }
    const handleCheckIncludeNoBooking = React.useCallback(() => {
      const currentUrlParams = new URLSearchParams(window.location.search)
      const currentValue = !includeNoBooking
      setIncludeNoBooking(currentValue)
      if (currentValue) {
        currentUrlParams.set('include_no_booking', 'true')
      } else {
        currentUrlParams.delete('include_no_booking')
      }
      location.href = `/agent/tours/weekly?${currentUrlParams.toString()}`
    }, [includeNoBooking])

    const filterLabels = React.useMemo(() => {
      return buildFilterLabels(filterOptions, posts)
    }, [filterOptions, posts])

    const changeToNextWeek = React.useCallback(() => {
      handleClickRangeButton(startDate.clone().add(7, 'days'))
    }, [startDate])

    const changeToPrevWeek = React.useCallback(() => {
      handleClickRangeButton(startDate.clone().subtract(7, 'days'))
    }, [startDate])

    const handleExportCSV = React.useCallback(() => {
      const query = buildGuideListExportQuery(startDate, LIMIT_GUIDE_ASSIGN_CSV_DAYS, filterOptions)
      const downloadUrl = `/agent/tour_guides/weekly_export.csv?${query}`
      window.open(downloadUrl, '_blank')
    }, [filterOptions, startDate])

    const dropdownRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
      if (!showDropdown) {
        return
      }
      const handleClickOutside = (event: MouseEvent) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
          setShowDropdown(false)
        }
      }
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    }, [showDropdown, setShowDropdown])

    const containerStyle = React.useMemo(() => {
      return `
        .opcalendar-header {
          width: 100%;
          max-width: 2048px;
          container-type: inline-size;
          container-name: opcalendar-header;
        }
        @container opcalendar-header (width < 1024px) {
          .filterContainer .button.filter {
            width: 100%;
            max-width: 240px;
          }
        }
        @container opcalendar-header (width < 960px) {
          .calendar-buttons {
            .button {
              padding: 0 8px;
              &.date-range {
                padding: 8px;
                min-width: max-content;
              }
            }
          }
        }
      `
    }, [])

    const handleDropdownButtonKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setShowDropdown(v => !v)
      }
      if (e.key === 'Escape') {
        setShowDropdown(false)
      }
    }

    const handleDropdownMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Escape') {
        setShowDropdown(false)
      }
    }

    return (
      <Settings className="opcalendar-header">
        <style type="text/css">{containerStyle}</style>
        <div className="button today" role="button" onClick={() => handleClickRangeButton(today)}>
          {I18n.t('generic.today', { locale: 'ja' })}
        </div>
        <div className="calendar-buttons">
          <div className="button prev" role="button" onClick={changeToPrevWeek}>
            <SvgIcon name="arrow_back" className="icon" ariaHidden={true} role="presentation" />
          </div>

          <div className="button date-range" role="button" onClick={() => setFocused(true)}>
            {currentDateLabel}
          </div>
          <CalendarSelectCustomStyle />
          <SingleDatePicker
            date={startDate}
            onDateChange={date => {
              if (date) {
                handleClickRangeButton(date)
                setFocused(false)
              }
            }}
            focused={focused}
            onFocusChange={changed => setFocused(changed.focused)}
            numberOfMonths={1}
            displayFormat="YYYY-MM-DD"
            isOutsideRange={() => false}
            hideKeyboardShortcutsPanel={true}
            noBorder={true}
            block={false}
            small={true}
            readOnly={true}
          />
          <div className="button next" role="button" onClick={changeToNextWeek}>
            <SvgIcon name="arrow_forward" className="icon" ariaHidden={true} role="presentation" />
          </div>
        </div>
        <div className="includeNoBooking">
          <CheckBox
            name="include_no_booking"
            label="未購入ツアー表示"
            showLabelInline={true}
            onChangeHandler={handleCheckIncludeNoBooking}
            checked={includeNoBooking}
          />
        </div>
        <div className="filterContainer">
          <div className="button filter" onClick={() => showFilterModal()} role="button">
            {filterLabels.length > 0 ? (
              filterLabels.map((option, index) => (
                <div className={`filterLabel ${option.type}`} key={index}>
                  {option.label}
                </div>
              ))
            ) : (
              <>
                <SvgIcon
                  name="filter_list"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
                {I18n.t('generic.filter_options', { locale: 'ja' })}
              </>
            )}
          </div>
          {filterLabels.length > 0 && (
            <div onClick={handleClickClearFilter} className="clearFilter" role="button">
              <SvgIcon name="close" className="icon" ariaHidden={true} role="presentation" />
            </div>
          )}
        </div>
        <div className="moreOptions" ref={dropdownRef}>
          <button
            className={`button moreOptionsButton${showDropdown ? ' active' : ''}`}
            onClick={() => setShowDropdown(!showDropdown)}
            onKeyDown={handleDropdownButtonKeyDown}
            role="button"
            aria-haspopup="true"
            aria-expanded={showDropdown}
            aria-label="More options"
            tabIndex={0}
            type="button"
          >
            <SvgIcon name="more_horiz" className="icon" ariaHidden={true} role="presentation" />
          </button>
          {showDropdown && (
            <div className="dropdownMenu" tabIndex={-1} onKeyDown={handleDropdownMenuKeyDown}>
              <button className="dropdownItem" onClick={handleExportCSV}>
                ガイド一覧CSV出力
              </button>
            </div>
          )}
        </div>
      </Settings>
    )
  }
)

const Settings = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  .calendar-buttons {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .button {
    display: flex;
    align-items: center;
    border: solid 1px ${constants.COLORS.divider};
    cursor: pointer;
    font-size: 14px;
    height: 40px;
    line-height: 40px;
    padding: 0 16px;
    transition: opacity 0.2s;
    border-radius: 8px;
    &.today {
      margin-left: 14px;
      margin-right: 14px;
      white-space: nowrap;
    }
    &.prev {
      border-radius: 8px 0 0 8px;
      border-right: none;
    }
    &.next {
      border-radius: 0 8px 8px 0;
    }
    &.date-range {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 206px;
      font-size: 14px;
      padding: 8px 16px;
      background-color: ${constants.COLORS.white};
      border-radius: 0;
      border-right: none;

      &:hover {
        background-color: ${constants.COLORS.cardBG};
      }
    }
    &.filter {
      max-width: 440px;
      min-width: 200px;
      padding-right: 44px;
      white-space: nowrap;
      overflow-x: auto;
      overflow-y: hidden;
      .icon {
        margin-right: 4px;
      }
      .filterLabel {
        position: relative;
        padding: 4px 8px;
        margin-right: 8px;
        height: 28px;
        line-height: 20px;
        font-size: 12px;
        border: 1px solid ${constants.COLORS.divider};
        border-radius: 4px;
        white-space: nowrap;
        &:last-child {
          margin-right: 0;
        }
        &::before {
          content: '';
          position: absolute;
          top: 50%;
          left: -1px;
          width: 4px;
          height: 16px;
          transform: translateY(-50%);
          border-radius: 0 2px 2px 0;
        }
        &.tour {
          &::before {
            background-color: ${constants.COLORS.primary};
          }
        }
        &.status {
          &::before {
            background-color: #f08a0d;
          }
        }
      }
    }
    &:hover {
      opacity: 0.6;
    }
  }
  .includeNoBooking {
    margin-left: 14px;
    .CheckBox_Icon {
      margin-right: 4px;
    }
    span {
      font-weight: normal;
      font-size: 13px;
    }
  }
  .filterContainer {
    position: relative;
    margin-left: auto;
    .clearFilter {
      display: flex;
      align-items: center;
      justify-content: center;
      position: absolute;
      top: 50%;
      right: 8px;
      width: 24px;
      height: 24px;
      background-color: ${constants.COLORS.primary};
      border-radius: 50%;
      transform: translateY(-50%);
      transition: background-color 0.2s;
      cursor: pointer;
      &:hover {
        background-color: ${constants.COLORS.secondary};
      }
      .icon {
        color: ${constants.COLORS.white};
        width: 16px;
        height: 16px;
      }
    }
  }
  .moreOptions {
    position: relative;
    margin-left: 12px;
    .moreOptionsButton {
      width: 32px;
      height: 32px;
      background: none;
      border: none;
      padding: 4px;
      border-radius: 50%;
      cursor: pointer;
      transition: background-color 0.2s;
      border: 1px solid ${constants.COLORS.divider};
      &.active {
        border-color: ${constants.COLORS.primary};
      }
      &:hover {
        background-color: ${constants.COLORS.cardBG};
      }
      .icon {
        width: 24px;
        height: 24px;
      }
    }
    .dropdownMenu {
      position: absolute;
      top: 32px;
      right: 0;
      padding: 8px;
      background-color: ${constants.COLORS.white};
      border-radius: 8px;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
      z-index: 10;
      min-width: 160px;
      width: max-content;
      .dropdownItem {
        width: 100%;
        padding: 12px 16px;
        background: none;
        border: none;
        text-align: left;
        font-size: 14px;
        cursor: pointer;
        transition: background-color 0.2s;
        border-radius: 8px;
        &:hover {
          background-color: ${constants.COLORS.cardBG};
        }
        &:disabled {
          color: ${constants.COLORS.divider};
          cursor: not-allowed;
          &:hover {
            background-color: transparent;
          }
        }
      }
    }
  }
`
// カレンダーの位置調整＋inputを消す
const CalendarSelectCustomStyle = createGlobalStyle`
  .SingleDatePicker {
    z-index: 2;
    position: absolute;
    top: -10px;
    right: 0;
    left: 0;
    margin: 0 auto;
    pointer-events: none;
  }

  .SingleDatePicker_picker {
    pointer-events: auto;
  }

  .DateInput_input, .DateInput__small {
    display: none;
    opacity: 0;
    pointer-events: none;
  }
`

export default WeeklySettings
